import { useState } from "react";

import { Button, Dialog, DialogClose, Notice, cn, focusRing } from "../../../../components/ui";
import { businessApi } from "../../../../services/api";
import { actionError } from "./hrPayroll";

const dayLabel = (date) => (date
  ? new Date(`${date}T00:00:00Z`).toLocaleDateString("en-PH", { timeZone: "UTC", month: "short", day: "numeric", year: "numeric" })
  : "");

/** "Jul 16 – Jul 31, 2026" — the dates a missing period would pay. */
export function windowDates(window) {
  const start = dayLabel(window.startDate);
  const end = dayLabel(window.endDate);
  const sameYear = window.startDate?.slice(0, 4) === window.endDate?.slice(0, 4);
  return `${sameYear ? start.replace(/, \d{4}$/, "") : start} – ${end}`;
}

/** Why a window cannot be added: the live periods it overlaps, in plain words. */
export function conflictText(window) {
  const names = (window.conflicts || []).map((item) => `"${item.name || `${item.startDate} – ${item.endDate}`}"`);
  return names.length ? `Overlaps ${names.join(" and ")}. These days are already in a payroll period.` : "";
}

/** One window of one pay schedule. Two schedules can have a window that starts on the same day. */
export const windowKey = (window) => `${window.scheduleId ?? "schedule"}:${window.startDate}`;

/**
 * AddMissingPeriodDialog (Payroll Gate 1, APP-HR-039 / 094 / 095 / 099; Gate 2: every pay schedule). Payroll periods
 * come from the pay schedules; HR adds one only when a schedule's window has no period. The server lists the
 * uncovered windows of each schedule and derives each period's dates and pay date — no free dates, no off-cycle run.
 */
export default function AddMissingPeriodDialog({ missing, onClose, onCreated }) {
  const windows = missing?.windows || [];
  const addable = windows.filter((item) => item.state === "missing");
  // The schedule is named on each row only when the list mixes schedules.
  const severalSchedules = new Set(windows.map((item) => item.scheduleId)).size > 1;
  const onlySchedule = severalSchedules ? null : (windows[0]?.scheduleName || missing?.schedule?.name || null);
  const [selected, setSelected] = useState(addable.length === 1 ? windowKey(addable[0]) : "");
  const [failure, setFailure] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    const chosen = addable.find((item) => windowKey(item) === selected);
    if (busy || !chosen) return;
    setBusy(true);
    setFailure("");
    try {
      const res = await businessApi.createPayrollPeriod({ payroll_schedule_id: chosen.scheduleId ?? missing?.schedule?.id, start_date: chosen.startDate });
      onCreated(res?.period || res?.data || null);
    } catch (error) {
      setFailure(actionError(error, "The payroll period couldn't be added. Try again."));
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => { if (!open && !busy) onClose(); }}
      title="Add a missing period"
      description={`Periods of ${onlySchedule ? `the ${onlySchedule}` : "your pay schedules"} that have no payroll yet. The dates and pay date come from the pay schedule.`}
      footer={
        <>
          <DialogClose asChild>
            <Button disabled={busy}>Cancel</Button>
          </DialogClose>
          <Button variant="primary" type="submit" form="add-missing-period" loading={busy} disabled={!selected}>Add period</Button>
        </>
      }
    >
      <form id="add-missing-period" noValidate onSubmit={submit} className="space-y-4">
        {windows.length ? (
          <fieldset>
            <legend className="sr-only">Missing periods</legend>
            <ul className="divide-y divide-subtle rounded-panel border border-default">
              {windows.map((window) => {
                const blocked = window.state !== "missing";
                const key = windowKey(window);
                const id = `missing-${key}`;
                return (
                  <li key={key}>
                    <label
                      htmlFor={id}
                      className={cn(
                        "flex min-h-touch items-start gap-3 px-3 py-2.5",
                        blocked ? "cursor-not-allowed text-secondary" : "cursor-pointer",
                      )}
                    >
                      <input
                        id={id}
                        type="radio"
                        name="missing-period"
                        value={key}
                        checked={selected === key}
                        disabled={blocked || busy}
                        onChange={() => setSelected(key)}
                        className={cn("mt-1", focusRing)}
                      />
                      <span className="min-w-0">
                        <span className="block font-medium text-foreground">{windowDates(window)}</span>
                        {severalSchedules ? <span className="block text-meta text-secondary">{window.scheduleName}</span> : null}
                        <span className="block text-meta text-secondary">
                          {blocked ? conflictText(window) : `Paid on ${dayLabel(window.payDate)}${window.current ? " · this is the current period" : ""}`}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </fieldset>
        ) : (
          <p className="text-body-sm text-secondary">No period is missing. Regular periods appear on their own from the pay schedule.</p>
        )}
        {failure ? <Notice tone="danger" title="Period not added" role="alert">{failure}</Notice> : null}
      </form>
    </Dialog>
  );
}
